import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { detailedInformationByAssetClass2 } from "./data/detailedInformationByAssetClass2";
import { ConditionalAdditionalFields } from "@/features/FormCard/DynamicCard";

import {
  AutocompleteField,
  AutocompleteLabelField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { NewDiff } from "@/features/FormCard/NewDiff";
import { useCurrencyOptions } from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import {
  dynamicFormFields,
  dynamicNewFormConfigWithoutId,
} from "./edit-form-conf";
import { fieldTranslations } from "./translations";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

export function N1StaticDataNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    isLoadingTranslations,
    options,
    translations,
  } = useDynamicForm({
    client: Clients.N1,
    table_name: Tables.N1_STATIC_DATA,
    formConfig: dynamicNewFormConfigWithoutId,
    isNew: true,
  });

  const currencyOptions = useCurrencyOptions();
  const assetClass2 = form.watch(dynamicFormFields.asset_class_2);
  const detailedInformationByAssetClass2WithTranslations = useMemo(
    () => detailedInformationByAssetClass2(t),
    [t],
  );

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Actif
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t(`${Tables.N1_STATIC_DATA}.sections.general_information`, {
                    ns: "tables",
                    defaultValue: "Informations générales",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  <div className="md:col-span-8">
                    <InputField
                      name={dynamicFormFields.name}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.name`, {
                        ns: "tables",
                        defaultValue: "Nom",
                      })}
                      isRequired={true}
                      control={form.control}
                    />
                  </div>
                  <div className="md:col-span-4">
                    <AutocompleteLabelField
                      name={dynamicFormFields.currency}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.currency`, {
                        ns: "tables",
                        defaultValue: "Devise",
                      })}
                      options={currencyOptions}
                      isRequired={true}
                      control={form.control}
                      className="min-w-0"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                  <InputField
                    name={dynamicFormFields.isin}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.isin`, {
                      ns: "tables",
                      defaultValue: "ISIN",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.code}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.ticker}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.ticker`, {
                      ns: "tables",
                      defaultValue: "Ticker",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteField
                    name={dynamicFormFields.asset_class_1}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.asset_class_1`, {
                      ns: "tables",
                    })}
                    options={options?.[dynamicFormFields.asset_class_1] ?? []}
                    control={form.control}
                    isRequired={true}
                  />
                  <AutocompleteField
                    name={dynamicFormFields.asset_class_2}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.asset_class_2`, {
                      ns: "tables",
                    })}
                    options={options?.[dynamicFormFields.asset_class_2] ?? []}
                    control={form.control}
                    isRequired={true}
                  />
                  <SelectField
                    name={dynamicFormFields.product}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.product`, {
                      ns: "tables",
                      defaultValue: "Produit",
                    })}
                    options={translations?.["product"]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                  <SelectField
                    name={dynamicFormFields.sector}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.sector`, {
                      ns: "tables",
                      defaultValue: "Secteur",
                    })}
                    options={translations?.[dynamicFormFields.sector]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name={dynamicFormFields.region}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.region`, {
                      ns: "tables",
                      defaultValue: "Région",
                    })}
                    options={translations?.[dynamicFormFields.region]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                  <InputField
                    name="category"
                    label={t(`${Tables.N1_STATIC_DATA}.columns.category`, {
                      ns: "tables",
                      defaultValue: "Catégorie",
                    })}
                    control={form.control}
                  />
                </div>
                <ConditionalAdditionalFields
                  fieldKey={assetClass2}
                  control={form.control}
                  isLoadingTranslations={isLoadingTranslations}
                  fieldsToDisplayByKey={
                    detailedInformationByAssetClass2WithTranslations
                  }
                />
                <Divider />
                <Label>
                  {t("sections.technical_info", {
                    defaultValue: "Informations techniques",
                  })}
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={dynamicFormFields.strategy_id}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.strategy_id`, {
                      ns: "tables",
                      defaultValue: "ID de la stratégie",
                    })}
                    control={form.control}
                    type="number"
                  />
                </div>
                <Divider />
                <Label>{t("sections.lpt_simulation")}</Label>
                <SelectField
                  name={dynamicFormFields.lpt_liquidity}
                  label={t(`${Tables.N1_STATIC_DATA}.columns.lpt_liquidity`, {
                    ns: "tables",
                    defaultValue: "Liquidités",
                  })}
                  options={translations?.[dynamicFormFields.lpt_liquidity]}
                  control={form.control}
                  allow_empty_option={true}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={dynamicFormFields.lpt_fund_vintage_start_date}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_fund_vintage_start_date`,
                      { ns: "tables", defaultValue: "Millésime" },
                    )}
                    control={form.control}
                  />
                  <DatePickerField
                    name={dynamicFormFields.lpt_investment_period_end_date}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_investment_period_end_date`,
                      {
                        ns: "tables",
                        defaultValue: "Fin de la période d'investissement",
                      },
                    )}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={dynamicFormFields.lpt_investment_duration}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_investment_duration`,
                      {
                        ns: "tables",
                        defaultValue: "Durée d'investissement",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.lpt_net_percentage_capital_called}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_net_percentage_capital_called`,
                      {
                        ns: "tables",
                        defaultValue: "Pourcentage net du capital appelé",
                      },
                    )}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={dynamicFormFields.lpt_loan_to_value}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_loan_to_value`,
                      {
                        ns: "tables",
                        defaultValue: "Ratio prêt-valeur",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.lpt_expected_distribution_rate}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_expected_distribution_rate`,
                      {
                        ns: "tables",
                        defaultValue: "Taux de distribution prévu",
                      },
                    )}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={dynamicFormFields.lpt_expected_investment_return}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_expected_investment_return`,
                      {
                        ns: "tables",
                        defaultValue: "Rendement d'investissement prévu",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name={dynamicFormFields.lpt_expected_annual_fee_rate}
                    label={t(
                      `${Tables.N1_STATIC_DATA}.columns.lpt_expected_annual_fee_rate`,
                      { ns: "tables", defaultValue: "Frais annuels attendus" },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  [dynamicFormFields.issuer_id],
                  fieldTranslations,
                  Tables.N1_STATIC_DATA,
                )
              : t("no_changes")
          }
        />

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
